"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.newRankingFilter = exports.newMeasureValueFilter = exports.newAllTimeFilter = exports.newRelativeDateFilter = exports.newAbsoluteDateFilter = exports.newNegativeAttributeFilter = exports.newPositiveAttributeFilter = void 0;
var tslib_1 = require("tslib");
// (C) 2019-2020 GoodData Corporation
var ts_invariant_1 = tslib_1.__importDefault(require("ts-invariant"));
var isNil_1 = tslib_1.__importDefault(require("lodash/isNil"));
var attribute_1 = require("../attribute");
var objRef_1 = require("../../objRef");
var measure_1 = require("../measure");
var factory_1 = require("../../objRef/factory");
/**
 * Creates a new positive attribute filter.
 *
 * NOTE: when specifying attribute element using URIs (primary keys), please keep in mind that they MAY NOT be transferable
 * across workspaces. On some backends (such as bear) same element WILL have different URI in each workspace.
 * In general we recommend using URIs only if your code retrieves them at runtime from backend using elements query
 * or from the data view's headers. Hardcoding URIs is never a good idea, if you find yourself doing that,
 * please consider specifying attribute elements by value
 *
 * @param attributeOrRef - either instance of attribute to create filter for or ref or identifier of attribute's display form
 * @param inValues - values to filter for; these can be either specified as AttributeElements object or as an array
 *  of attribute element _values_; if you specify empty array, then the filter will be noop and will be ignored
 * @public
 */
function newPositiveAttributeFilter(attributeOrRef, inValues) {
    var objRef = objRef_1.isObjRef(attributeOrRef)
        ? attributeOrRef
        : typeof attributeOrRef === "string"
            ? factory_1.idRef(attributeOrRef)
            : attribute_1.attributeDisplayFormRef(attributeOrRef);
    var inObject = Array.isArray(inValues) ? { values: inValues } : inValues;
    return {
        positiveAttributeFilter: {
            displayForm: objRef,
            in: inObject,
        },
    };
}
exports.newPositiveAttributeFilter = newPositiveAttributeFilter;
/**
 * Creates a new negative attribute filter.
 *
 * NOTE: when specifying attribute element using URIs (primary keys), please keep in mind that they MAY NOT be transferable
 * across workspaces. On some backends (such as bear) same element WILL have different URI in each workspace.
 * In general we recommend using URIs only if your code retrieves them at runtime from backend using elements query
 * or from the data view's headers. Hardcoding URIs is never a good idea, if you find yourself doing that,
 * please consider specifying attribute elements by value
 *
 * @param attributeOrRef - either instance of attribute to create filter for or ref or identifier of attribute's display form
 * @param notInValues - values to filter out; these can be either specified as AttributeElements object or as an array
 *  of attribute element _values_; if you specify empty array, then the filter will be noop and will be ignored
 * @public
 */
function newNegativeAttributeFilter(attributeOrRef, notInValues) {
    var objRef = objRef_1.isObjRef(attributeOrRef)
        ? attributeOrRef
        : typeof attributeOrRef === "string"
            ? factory_1.idRef(attributeOrRef)
            : attribute_1.attributeDisplayFormRef(attributeOrRef);
    var notInObject = Array.isArray(notInValues)
        ? { values: notInValues }
        : notInValues;
    return {
        negativeAttributeFilter: {
            displayForm: objRef,
            notIn: notInObject,
        },
    };
}
exports.newNegativeAttributeFilter = newNegativeAttributeFilter;
/**
 * Creates a new absolute date filter.
 *
 * @param dateDataSet - ref or identifier of the date data set to filter on
 * @param from - start of the interval in ISO-8601 calendar date format
 * @param to - end of the interval in ISO-8601 calendar date format
 * @public
 */
function newAbsoluteDateFilter(dateDataSet, from, to) {
    var dataSet = objRef_1.isObjRef(dateDataSet) ? dateDataSet : factory_1.idRef(dateDataSet);
    return {
        absoluteDateFilter: {
            dataSet: dataSet,
            from: from,
            to: to,
        },
    };
}
exports.newAbsoluteDateFilter = newAbsoluteDateFilter;
/**
 * Creates a new relative date filter.
 *
 * @param dateDataSet - ref or identifier of the date data set to filter on
 * @param granularity - granularity of the filters (month, year, etc.)
 * @param from - start of the interval – negative numbers mean the past, zero means today, positive numbers mean the future
 * @param to - end of the interval – negative numbers mean the past, zero means today, positive numbers mean the future
 *
 * See also {@link DateAttributeGranularity} and {@link DateGranularity}
 * @public
 */
function newRelativeDateFilter(dateDataSet, granularity, from, to) {
    var dataSet = objRef_1.isObjRef(dateDataSet) ? dateDataSet : factory_1.idRef(dateDataSet);
    return {
        relativeDateFilter: {
            dataSet: dataSet,
            granularity: granularity,
            from: from,
            to: to,
        },
    };
}
exports.newRelativeDateFilter = newRelativeDateFilter;
/**
 * Creates a new all time date filter. This filter is used to indicate that there should be no filtering on the given date data set.
 *
 * @param dateDataSet - ref or identifier of the date data set to filter on
 * @public
 */
function newAllTimeFilter(dateDataSet) {
    var dataSet = objRef_1.isObjRef(dateDataSet) ? dateDataSet : factory_1.idRef(dateDataSet);
    return {
        relativeDateFilter: {
            dataSet: dataSet,
            granularity: "ALL_TIME_GRANULARITY",
            from: 0,
            to: 0,
        },
    };
}
exports.newAllTimeFilter = newAllTimeFilter;
function convertMeasureOrRefToObjRefInScope(measureOrRef) {
    return measure_1.isMeasure(measureOrRef)
        ? { localIdentifier: measure_1.measureLocalId(measureOrRef) }
        : typeof measureOrRef === "string"
            ? factory_1.localIdRef(measureOrRef)
            : measureOrRef;
}
function convertAttributeOrRefToObjRefInScope(attributeOrRef) {
    return attribute_1.isAttribute(attributeOrRef)
        ? { localIdentifier: attribute_1.attributeLocalId(attributeOrRef) }
        : typeof attributeOrRef === "string"
            ? factory_1.localIdRef(attributeOrRef)
            : attributeOrRef;
}
/**
 * Creates a new measure value filter.
 *
 * @param measureOrRef - instance of measure to filter, or reference of the measure object; if instance of measure is
 *   provided, then it is assumed this measure is in scope of execution and will be referenced by the filter by
 *   its local identifier
 * @param operator - comparison or range operator to use in the filter
 * @param val1 - first numeric value, used as value in comparison or as 'from' value in range operators
 * @param val2OrTreatNullValuesAsInComparison - second numeric value, required in range operators and used in 'to' value; optional in comparison operators used as 'treatNullValuesAs' value
 * @param treatNullValuesAsInRange - third numeric value, optional in range operators and used as 'treatNullValuesAs' value; optional and ignored in comparison operators
 * @public
 */
function newMeasureValueFilter(measureOrRef, operator, val1, val2OrTreatNullValuesAsInComparison, treatNullValuesAsInRange) {
    var ref = convertMeasureOrRefToObjRefInScope(measureOrRef);
    if (operator === "BETWEEN" || operator === "NOT_BETWEEN") {
        ts_invariant_1.default(val2OrTreatNullValuesAsInComparison !== undefined, "measure value filter with range operator requires two numeric values");
        var nullValuesProp = !isNil_1.default(treatNullValuesAsInRange)
            ? { treatNullValuesAs: treatNullValuesAsInRange }
            : {};
        return {
            measureValueFilter: {
                measure: ref,
                condition: {
                    range: tslib_1.__assign({ operator: operator, from: val1, to: val2OrTreatNullValuesAsInComparison }, nullValuesProp),
                },
            },
        };
    }
    else {
        var nullValuesProp = !isNil_1.default(val2OrTreatNullValuesAsInComparison)
            ? { treatNullValuesAs: val2OrTreatNullValuesAsInComparison }
            : {};
        return {
            measureValueFilter: {
                measure: ref,
                condition: {
                    comparison: tslib_1.__assign({ operator: operator, value: val1 }, nullValuesProp),
                },
            },
        };
    }
}
exports.newMeasureValueFilter = newMeasureValueFilter;
/**
 * Creates a new ranking filter.
 *
 * @param measureOrRef - instance of measure to filter, or reference of the measure object; if instance of measure is
 *   provided, then it is assumed this measure is in scope of execution and will be referenced by the filter by
 *   its local identifier
 * @param attributesOrRefsOrOperator - Array of attributes used in filter, or reference of the attribute object. If instance of attribute is
 *   provided, then it is assumed this attribute is in scope of execution and will be referenced by the filter by
 *   its local identifier. If attributes are not specified it's TOP or BOTTOM operator to use in the filter
 * @param operatorOrValue - Number of values to use in filter or operator if attributes are not speciied
 * @param valueOrNothing - Value or nothing if attributes are not specified
 * @public
 */
function newRankingFilter(measureOrRef, attributesOrRefsOrOperator, operatorOrValue, valueOrNothing) {
    if (typeof attributesOrRefsOrOperator === "string" && typeof operatorOrValue === "number") {
        var measureRef = convertMeasureOrRefToObjRefInScope(measureOrRef);
        return {
            rankingFilter: {
                measure: measureRef,
                operator: attributesOrRefsOrOperator,
                value: operatorOrValue,
            },
        };
    }
    else if (typeof operatorOrValue === "string" && valueOrNothing) {
        var measureRef = convertMeasureOrRefToObjRefInScope(measureOrRef);
        var attributeRefs = attributesOrRefsOrOperator.map(convertAttributeOrRefToObjRefInScope);
        var attributesProp = attributeRefs.length ? { attributes: attributeRefs } : {};
        return {
            rankingFilter: tslib_1.__assign({ measure: measureRef, operator: operatorOrValue, value: valueOrNothing }, attributesProp),
        };
    }
    throw new Error("Ranking filter requires measure, operator and value");
}
exports.newRankingFilter = newRankingFilter;
//# sourceMappingURL=factory.js.map